import { useEffect, useState } from "react";
import {
  Alert, Button, Checkbox, Group, Loader, Modal, NumberInput, ScrollArea, SegmentedControl,
  Stack, Table, Text, TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { PassportCandidate } from "../../api/types";
import { localAgentPrintJobs } from "../../api/localAgent";
import { useLocalAgent } from "../../hooks/useLocalAgent";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

export function PrintPassportLabelsModal({
  opened, onClose, shipmentId,
}: { opened: boolean; onClose: () => void; shipmentId?: number }) {
  const { t } = useTranslation();
  const localAgent = useLocalAgent();
  const [source, setSource] = useState<"shipment" | "range" | "single">(shipmentId ? "shipment" : "range");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<PassportCandidate[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [delaySec, setDelaySec] = useState<number | string>(1.2);
  const [what, setWhat] = useState<"passport" | "device_passport" | "device">("passport");
  const kinds = what === "device_passport" ? ["device", "passport"]
    : what === "device" ? ["device"] : ["passport"];

  useEffect(() => {
    if (opened && shipmentId && source === "shipment") void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, source]);

  async function search() {
    setLoading(true);
    try {
      const params = source === "shipment" ? { shipment_id: shipmentId }
        : source === "range" ? { from: from.trim(), to: to.trim() }
        : { q: q.trim() };
      const res = await api.listPassportCandidates(params);
      setRows(res.devices);
      setChecked(new Set(res.devices.map((d) => d.device_id)));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  function toggle(id: number) {
    setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function print() {
    const ids = rows.map((r) => r.device_id).filter((id) => checked.has(id));
    if (ids.length === 0) return;
    const delayMs = Math.round((Number(delaySec) || 0) * 1000);
    setPrinting(true);
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintPassportsPayload(ids, "small", delayMs, kinds);
        await localAgentPrintJobs(r.jobs, r.delay_ms, localAgent.selectedId);
      } else {
        const r = await api.godexPrintPassports(ids, "small", delayMs, kinds);
        // Server path can partially fail (printer error mid-batch): report it.
        if (r.error) {
          notifications.show({
            color: "yellow",
            message: t("passportLabels.partial", { sent: r.labels_sent, total: r.labels_total }),
          });
          return;
        }
      }
      notifications.show({ color: "green", message: t("passportLabels.sent", { count: ids.length }) });
      onClose();
    } catch (e) {
      const detail = e instanceof ApiError ? (e.detail as { detail?: string; error?: string } | null) : null;
      notifications.show({ color: "red", message: detail?.detail || detail?.error || String(e) });
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("passportLabels.titleGeneric")} size="lg">
      <Stack>
        <SegmentedControl
          value={source}
          onChange={(v) => { setSource(v as typeof source); setRows([]); setChecked(new Set()); }}
          data={[
            ...(shipmentId ? [{ value: "shipment", label: t("passportLabels.srcShipment") }] : []),
            { value: "range", label: t("passportLabels.srcRange") },
            { value: "single", label: t("passportLabels.srcSingle") },
          ]}
        />
        <SegmentedControl
          value={what}
          onChange={(v) => setWhat(v as typeof what)}
          data={[
            { value: "passport", label: t("passportLabels.whatPassport") },
            { value: "device_passport", label: t("passportLabels.whatBoth") },
            { value: "device", label: t("passportLabels.whatDevice") },
          ]}
        />
        {source === "range" && (
          <Group align="end">
            <TextInput label={t("passportLabels.from")} placeholder="13800" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            <TextInput label={t("passportLabels.to")} placeholder="13899" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
            <Button onClick={() => void search()} loading={loading}>{t("passportLabels.search")}</Button>
          </Group>
        )}
        {source === "single" && (
          <Group align="end">
            <TextInput style={{ flex: 1 }} label={t("passportLabels.single")} placeholder="DevEUI / serial / model" value={q} onChange={(e) => setQ(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === "Enter") void search(); }} />
            <Button onClick={() => void search()} loading={loading}>{t("passportLabels.search")}</Button>
          </Group>
        )}

        {loading ? <Loader /> : rows.length === 0 ? (
          <Text c="dimmed" size="sm">{t("passportLabels.none")}</Text>
        ) : (
          <>
            <Text size="sm">{t("passportLabels.found", { checked: checked.size, total: rows.length })}</Text>
            <ScrollArea h={320}>
            <ResponsiveTable
              stickyColumns={2}
              mobile={{
                rows,
                rowKey: (d) => d.device_id,
                title: (d) => d.dev_eui.toLowerCase(),
                subtitle: (d) => d.model ?? "—",
                fields: [{ label: t("passportLabels.colShipment"), render: (d) => d.shipment_code ?? "—" }],
                selection: { isSelected: (d) => checked.has(d.device_id), toggle: (d) => toggle(d.device_id) },
                empty: t("passportLabels.none"),
              }}
            >
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={36} />
                    <Table.Th>{t("passportLabels.colModel")}</Table.Th>
                    <Table.Th>DEV_EUI</Table.Th>
                    <Table.Th>{t("passportLabels.colShipment")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((d) => (
                    <Table.Tr key={d.device_id}>
                      <Table.Td><Checkbox checked={checked.has(d.device_id)} onChange={() => toggle(d.device_id)} /></Table.Td>
                      <Table.Td>{d.model ?? "—"}</Table.Td>
                      <Table.Td ff="monospace">{d.dev_eui.toLowerCase()}</Table.Td>
                      <Table.Td>{d.shipment_code ?? "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ResponsiveTable>
            </ScrollArea>
            {rows.length >= 2000 && <Alert color="yellow">{t("passportLabels.capped")}</Alert>}
            <Group justify="flex-end" align="end">
              <NumberInput
                label={t("passportLabels.delay")}
                description={t("passportLabels.delayHint")}
                value={delaySec}
                onChange={setDelaySec}
                min={0}
                max={10}
                step={0.1}
                decimalScale={1}
                w={150}
              />
              <Button onClick={() => void print()} loading={printing} disabled={checked.size === 0}>
                {t("passportLabels.print", { count: checked.size * kinds.length })}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
